import { useIntl } from 'react-intl';
import { Badge, Header, Icon } from '@amzn/awsui-components-react';

import { IdPs } from '../constants/providers';
import { providerMessages } from '../i18n/provider.messages';
import { IdProvider } from '../types/auth';
import bidIcon from '../assets/AWS_logo_RGB_light_mode.svg';
import lwaIcon from '../assets/lwa.svg';
import styles from './ProviderButton.module.css';
import { Button } from '@amzn/awsui-components-react/polaris';
import signinMessages from '../i18n/signin.messages';
import React from 'react';
import IdpDescription from './IdpDescription';
import Alert from '@amzn/awsui-components-react/polaris/alert';
import apnDeprecationUXMessages from '../i18n/apnDeprecationUX.messages';

const ProviderButton = ({
  provider,
  onClick,
  lastUsedIdp,
  isMobile,
  enableApnDeprecationAlert,
  alternativeMethod,
  ...props
}: {
  provider: IdProvider;
  onClick: Function;
  lastUsedIdp?: boolean;
  isMobile?: boolean;
  enableApnDeprecationAlert?: boolean;
  alternativeMethod?: boolean;
}) => {
  const { formatMessage } = useIntl();

  const providerButtonLabel =
    provider.idp === IdPs.AWSBuilderID
      ? formatMessage(signinMessages.createOrSignInButton)
      : formatMessage(signinMessages.signinButton);

  const providerHeaderLabel = IdPs[provider.idp as IdPs]
    ? formatMessage(providerMessages[provider.idp as IdPs], {
        i: (str) => <i>{str}</i>,
      })
    : provider.idp;

  // apply styling depending on the provider
  let buttonStyle: string | undefined;
  let iconUrl = '';
  switch (provider.idp) {
    case IdPs.AWSBuilderID:
      iconUrl = bidIcon;
      buttonStyle = !alternativeMethod
        ? styles.siginInButtonWithDescription
        : undefined;
      break;
    case IdPs.LoginWithAmazon:
      iconUrl = lwaIcon;
      buttonStyle = styles.siginInButtonWithDescription;
      break;
  }
  if (provider.idp === IdPs.LoginWithAmazon) {
    return <></>;
  }
  return (
    <div
      className={
        IdPs.AmazonFederate === provider.idp && lastUsedIdp === false
          ? styles.idpBox_last
          : styles.idpBox
      }
    >
      <div className={[styles.rowElements].join(' ')}>
        {iconUrl && !isMobile ? (
          <div className={[styles.bid].join('')}>
            <Icon
              size="large"
              url={iconUrl}
              alt={formatMessage(providerMessages[IdPs.AWSBuilderID])}
            />
          </div>
        ) : (
          <></>
        )}
        <div className={styles.idpHeader}>
          <Header
            id={`${provider.idp}-providerLabel`}
            data-testid="signin-providerLabel"
            variant="h2"
            description={IdpDescription({
              provider,
              isMobile,
              alternativeMethod,
            })}
          >
            <div className={styles.headerContent}>
              <div className={styles.headerTextWrapper}>
                <span
                  className={styles.headerText}
                  data-testid={`${provider.idp}-identity-provider-label`}
                >
                  {provider.lastUsedIdpDisplayName
                    ? provider.lastUsedIdpDisplayName
                    : IdPs[provider.idp as IdPs]
                    ? formatMessage(providerMessages[provider.idp as IdPs], {
                        i: (str) => <i>{str}</i>,
                      })
                    : provider.idp}
                </span>
                {!alternativeMethod &&
                  (provider.idp === IdPs.AWSBuilderID ||
                    provider.idp === IdPs.AWSPartnerNetwork) && (
                    <Badge className={styles.customBadge} color="blue">
                      {provider.idp === IdPs.AWSBuilderID
                        ? formatMessage(signinMessages.builderIdRecommendation)
                        : formatMessage(signinMessages.apnBadgeRecommendation)}
                    </Badge>
                  )}
              </div>
            </div>
          </Header>
        </div>

        <div
          className={
            buttonStyle === undefined
              ? styles.siginIn
              : styles.siginInButtonWithDescription
          }
        >
          <Button
            wrapText={false}
            onClick={() => onClick(provider)}
            data-testid={`${provider.idp}-provider-button`}
            id={`${provider.idp}-provider-button`}
            ariaLabel={`${providerButtonLabel} ${providerHeaderLabel}`}
          >
            {providerButtonLabel}
          </Button>
        </div>
      </div>
      {/* APN provider */}
      {enableApnDeprecationAlert && provider.idp === IdPs.AWSPartnerNetwork && (
        <Alert
          statusIconAriaLabel={'Info'}
          className={styles.apnAlert}
          data-testid={'apn-deprecation-banner'}
        >
          {formatMessage(apnDeprecationUXMessages.alertContent)}
        </Alert>
      )}
    </div>
  );
};

export default ProviderButton;
